import React, { FC } from "react";
import { Stack, Link, Box } from "@mui/material";

import { Colors } from "@theme/colors";
import { MenuLink } from "@src/types";
import HeaderLink from "./link";
import { consoleLoginURL, addUrlParams, addParameters } from "@src/urls";
import { ContainedSignup } from "./buttons";
import { ArrowForwardRounded } from "@mui/icons-material";
import {
  filterFooterNavItems,
  uiContainerTransitionMs,
  uiOpacityHover,
} from "@src/utils";

interface Props {
  isHome: boolean;
  reverseColor: boolean;
  menuLinks: MenuLink[];
  pageContext?: any;
}

const calculateLoginUrlWithParameters = (
  url: string = "",
  slug = "",
  pageUtms = {},
) => {
  try {
    const pathParts = location.pathname.split("/").filter(Boolean);

    // PAN-18006: If we are on a blog post specifically, update our nav URL
    if (pathParts[0] === "blog" && pathParts.length === 2 && slug) {
      url = addParameters(url, {
        referrer: `blog-${slug}`,
      });
    }

    // Apply any page-level UTMs, overwrite the previous values
    url = addParameters(url, {
      ...pageUtms,
    });
  } catch (_ignore) {
    // Fail gracefully if we encounter something unexpected
  }

  return url;
};

const TopNav: FC<Props> = ({
  isHome,
  reverseColor,
  menuLinks,
  pageContext = {},
}) => {
  const loginUrl = calculateLoginUrlWithParameters(
    addUrlParams(consoleLoginURL),
    pageContext?.slug,
    pageContext?.pageUtms ?? {},
  );

  const textColor =
    isHome || reverseColor ? Colors.PrimaryMain : Colors.PrimaryContrast;
  const logoColor =
    isHome || reverseColor ? Colors.PrimaryContrast : Colors.PrimaryMain;

  const fontStyles = {
    fontWeight: 600,
    fontFamily: "Inter !important",
    letterSpacing: "-0.25px",
    fontSize: "15px",
  };

  return (
    <>
      <Stack
        direction="row"
        gap={{ xs: "10px", sm: "10px", md: "23px", lg: "40px" }}
        sx={{
          paddingLeft: {
            xs: "0px",
            sm: "40px",
          },
          ...fontStyles,
          "*": {
            ...fontStyles,
          },
        }}
      >
        {menuLinks.filter(filterFooterNavItems).map((link: any) => {
          return (
            <HeaderLink
              isHome={isHome}
              reverseColor={reverseColor}
              link={link}
              key={`nav-item-${link.name}`}
            />
          );
        })}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
          flex: "1 1 0px",
          justifyContent: "flex-end",
        }}
      >
        <Box
          sx={{
            opacity: 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            "&:hover": {
              opacity: uiOpacityHover,
            },
          }}
        >
          <Link
            href="/contact-us/"
            sx={{
              color: logoColor,
              display: "inline-flex",
              alignItems: "center",
              ...fontStyles,
              textDecoration: "none",
              "&:hover": {
                color: logoColor,
                textDecoration: "none",
              },
              "&:visited": {
                color: logoColor,
                textDecoration: "none",
              },
            }}
          >
            Contact Us{" "}
            <ArrowForwardRounded
              sx={{
                width: "12px",
                height: "12px",
                marginLeft: "5px",
              }}
            />
          </Link>
        </Box>

        <ContainedSignup
          url={loginUrl}
          bgColor={logoColor}
          textColor={textColor}
          sx={fontStyles}
        >
          Log In
        </ContainedSignup>
      </Stack>
    </>
  );
};

export default TopNav;
